/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, Heading, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { ErrorField, Form, FormRenderProps } from 'components/form'
import { HLabel } from 'components/HLabel'
import { LoadingIndicator } from 'components/loading/LoadingIndicator'
import { TableRowMenu, TableRowMenuButton } from 'components/table'
import { FormApi } from 'final-form'
import { useProblemaCondicaoSelectFieldQuery } from 'graphql/hooks.generated'
import { SituacaoProblema } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { EditableList, EditableRow, useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { Observacao } from 'view/atendimentos/detail/components/Observacao'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'
import { SituacaoProblemaTag } from 'view/atendimentos/detail/components/SituacaoProblemaTag'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { ProblemaCondicaoModel } from './model'
import { ProblemasCondicoesForm } from './ProblemasCondicoesForm'

const meta = metaPath<ProblemaCondicaoModel>()

export interface ProblemasCondicoesFieldProps<T> {
  name: MetaArray<ProblemaCondicaoModel>
  somenteCiap?: boolean
  cidadao?: CidadaoAtendimento
  prontuarioId: ID
  dataAtendimento: Instant
}

export function ProblemasCondicoesField<T = any>(props: ProblemasCondicoesFieldProps<T>) {
  const { name, somenteCiap, cidadao, prontuarioId, dataAtendimento } = props

  const {
    handleSubmit,
    handleRowChanged,
    removeItem,
    input: { value },
  } = useEditableListField({
    name,
  })

  const alert = useAlert()

  const handleAddItem = (values: ProblemaCondicaoModel, api: FormApi<ProblemaCondicaoModel>) => {
    handleSubmit(values, api)
    values?.incluirNaListaProblemas && alert('success', alertMessageAddProblema(values))
  }

  const handleEditItem = (values: ProblemaCondicaoModel) => {
    handleRowChanged(values)
    values?.incluirNaListaProblemas && alert('success', alertMessageEditProblema(values))
  }

  const handleRemoveItem = (itemToRemove: ProblemaCondicaoModel) => {
    removeItem(itemToRemove)
    itemToRemove?.incluirNaListaProblemas && alert('success', alertMessageRemoveProblema(itemToRemove))
  }

  const {
    loading,
    data: { problemas },
  } = useProblemaCondicaoSelectFieldQuery({
    variables: {
      filtro: {
        prontuarioId: prontuarioId,
        situacoes: [SituacaoProblema.ATIVO, SituacaoProblema.LATENTE],
      },
    },
  })

  if (loading) {
    return <LoadingIndicator />
  }

  return (
    <VFlow vSpacing={0.5}>
      <ErrorField name={name} />
      <EditableList>
        <ProblemasCondicoesForm
          onSubmit={handleAddItem}
          somenteCiap={somenteCiap}
          formValues={value}
          prontuarioId={prontuarioId}
          cidadao={cidadao}
          dataAtendimento={dataAtendimento}
          problemasCidadao={problemas?.content}
        />

        {value?.length > 0 && (
          <div>
            <Grid
              style={css`
                margin: 0;
                padding: 0 1rem;
              `}
            >
              <Cell size={somenteCiap ? 6 : 4}>
                <Text fontWeight='bold'>CIAP 2</Text>
              </Cell>
              {!somenteCiap && (
                <Cell size={3}>
                  <Text fontWeight='bold'>CID 10</Text>
                </Cell>
              )}
              <Cell size={5}>
                <Text fontWeight='bold'>Lista de problemas/condições</Text>
              </Cell>
            </Grid>
          </div>
        )}
        {value?.length > 0 &&
          value.map((item) => (
            <CiapCidRow
              key={item._id}
              model={item}
              onChange={handleEditItem}
              onRemove={handleRemoveItem}
              somenteCiap={somenteCiap}
              testValues={value}
              cidadao={props.cidadao}
              dataAtendimento={props.dataAtendimento}
              prontuarioId={props.prontuarioId}
            />
          ))}
      </EditableList>
    </VFlow>
  )
}

interface CiapCidRowProps {
  cidadao: CidadaoAtendimento
  model: ProblemaCondicaoModel
  prontuarioId: ID
  somenteCiap: boolean
  testValues: Array<ProblemaCondicaoModel>

  onChange(values: ProblemaCondicaoModel): void

  onRemove(item: ProblemaCondicaoModel): void

  dataAtendimento: Instant
}

const validator = createValidator<ProblemaCondicaoModel>({
  observacao: [maxLength(200)],
})

type CiapCidRowState = 'default' | 'observacao' | 'edicao'

function CiapCidRow(props: CiapCidRowProps) {
  const { model, somenteCiap, testValues, onChange, onRemove } = props
  const [state, setState] = useState<CiapCidRowState>('default')

  const handleExibirLinha = () => setState('default')
  const handleEditarObservacao = () => setState('observacao')
  const handleEditar = () => setState('edicao')

  const handleRemove = () => onRemove(model)

  const handleSubmit = (values: ProblemaCondicaoModel, formApi: FormApi) => {
    onChange(values)
    setTimeout(formApi.reset)
    handleExibirLinha()
  }

  const renderForm = (formRenderProps: FormRenderProps<ProblemaCondicaoModel>) => (
    <VFlow
      vSpacing={0.5}
      style={css`
        padding: 1rem 0;
      `}
    >
      <Heading level={4}>Adicionar observação</Heading>
      <Grid
        style={css`
          margin: 0;
        `}
      >
        {model.ciap && (
          <Cell size={4} flexGrow={1}>
            <HLabel title='CIAP 2:'>{`${model.ciap.nome} - ${model.ciap.codigo}`}</HLabel>
          </Cell>
        )}
        {model.cid && (
          <Cell size={3} flexGrow={1}>
            <HLabel title='CID 10:'>{`${model.cid.nome} - ${model.cid.codigo}`}</HLabel>
          </Cell>
        )}
      </Grid>
      <ObservacaoFormGrid name={meta.observacao} onCancel={handleExibirLinha} onSubmit={formRenderProps.handleSubmit} />
    </VFlow>
  )

  return (
    <EditableRow editing={state !== 'default'}>
      {state === 'default' && (
        <Fragment>
          <Grid
            style={css`
              margin: 0;
            `}
          >
            <Cell size={somenteCiap ? 6 : 4} alignSelf='center'>
              {model.ciap ? `${model.ciap.nome} - ${model.ciap.codigo}` : '-'}
            </Cell>
            {!somenteCiap && (
              <Cell size={3} alignSelf='center'>
                {model.cid ? `${model.cid.nome} - ${model.cid.codigo}` : '-'}
              </Cell>
            )}
            <Cell size={2} alignSelf='center' flexGrow={1}>
              {model.incluirNaListaProblemas && model.situacaoProblema ? (
                <SituacaoProblemaTag situacao={model.situacaoProblema} />
              ) : (
                <span>Não incluído</span>
              )}
            </Cell>
            <Cell
              size={3}
              alignSelf='center'
              style={css`
                padding: 0.25rem 0;
                text-align: right;
              `}
            >
              <TableRowMenu>
                <TableRowMenuButton
                  text={model.observacao ? 'Editar observação' : 'Adicionar observação'}
                  icon='chatOutline'
                  onClick={handleEditarObservacao}
                />
                <TableRowMenuButton text='Editar' icon='penOutline' onClick={handleEditar} />
                <TableRowMenuButton text='Excluir' icon='trashOutline' onClick={handleRemove} />
              </TableRowMenu>
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} />
        </Fragment>
      )}
      {state === 'observacao' && (
        <Form<ProblemaCondicaoModel>
          render={renderForm}
          initialValues={model}
          validate={validator}
          onSubmit={handleSubmit}
        />
      )}
      {state === 'edicao' && (
        <VFlow
          style={css`
            padding: 1rem 0;
          `}
        >
          <Heading level={4}>Editar problema / condição</Heading>
          <ProblemasCondicoesForm
            edicao
            initialValues={model}
            onCancel={handleExibirLinha}
            onSubmit={handleSubmit}
            formValues={testValues}
            cidadao={props.cidadao}
            dataAtendimento={props.dataAtendimento}
            prontuarioId={props.prontuarioId}
          />
        </VFlow>
      )}
    </EditableRow>
  )
}

const alertMessageAddProblema = (model: ProblemaCondicaoModel) =>
  model.problemaCondicaoEvoluir
    ? `A avaliação do problema/condição${cidCiapString(model)}foi incluída na Lista de problemas/condições.`
    : `O Problema/condição${cidCiapString(model)}foi incluído na Lista de problemas/condições.`

const alertMessageEditProblema = (model: ProblemaCondicaoModel) =>
  `A avaliação do problema/condição${cidCiapString(model)}foi alterada na Lista de problemas/condições.`

const alertMessageRemoveProblema = (model: ProblemaCondicaoModel) =>
  model.problemaCondicaoEvoluir
    ? `A avaliação do problema/condição${cidCiapString(model)}foi removida da Lista de problemas/condições.`
    : `O problema/condição${cidCiapString(model)}foi removido da Lista de problemas/condições.`

const cidCiapString = (model: ProblemaCondicaoModel) => {
  const ciap = model.ciap ? ` ${model.ciap.codigo} (CIAP2) ` : ''
  const cid = model.cid ? ` ${model.cid.codigo} (CID10) ` : ''

  return `${ciap}${ciap && cid && '-'}${cid}`
}
